import React from 'react';
import { Building2, CircleHelp, House, Layers3, TriangleAlert } from 'lucide-react';

import { cn } from '../lib/utils';

export const PROGRAM_CONTEXTS = [
  'SAH',
  'RAC',
  'MIXED',
  'SHARED',
  'OTHER',
  'UNKNOWN',
  'CONFLICT',
] as const;
export type ProgramContext = (typeof PROGRAM_CONTEXTS)[number];

const PRESENTATION = {
  SAH: {
    label: 'Support at Home',
    Icon: House,
    legacyAccentClass: 'text-statura-green-accessible',
  },
  RAC: {
    label: 'Residential aged care',
    Icon: Building2,
    legacyAccentClass: 'text-statura-ink',
  },
  MIXED: {
    label: 'Mixed care programs',
    Icon: Layers3,
    legacyAccentClass: 'text-text-body',
  },
  SHARED: {
    label: 'Shared across programs',
    Icon: Layers3,
    legacyAccentClass: 'text-text-body',
  },
  OTHER: {
    label: 'Other care program',
    Icon: CircleHelp,
    legacyAccentClass: 'text-text-body',
  },
  UNKNOWN: {
    label: 'Program not confirmed',
    Icon: CircleHelp,
    legacyAccentClass: 'text-text-body',
  },
  CONFLICT: {
    label: 'Program selection required',
    Icon: TriangleAlert,
    legacyAccentClass: 'text-text-body',
  },
} as const;

export interface ProgramContextIndicatorProps extends React.HTMLAttributes<HTMLSpanElement> {
  program: ProgramContext;
  /** Optional supplementary context; never replaces or contradicts the canonical program label. */
  detail?: string;
  compact?: boolean;
}

const CANONICAL_PROGRAM_LABELS = new Set(
  Object.values(PRESENTATION).map((presentation) => presentation.label.toLocaleLowerCase()),
);

export function normaliseProgramContextDetail(
  program: ProgramContext,
  detail: string | undefined,
): string | null {
  const normalized = detail?.replace(/\s+/gu, ' ').trim() ?? '';
  if (!normalized) return null;
  const lower = normalized.toLocaleLowerCase();
  const ownLabel = PRESENTATION[program].label.toLocaleLowerCase();
  if (CANONICAL_PROGRAM_LABELS.has(lower) && lower !== ownLabel) return null;
  return normalized;
}

/** Program context always includes visible text; semantic colour is secondary only. */
export function ProgramContextIndicator({
  program,
  detail,
  compact = false,
  className,
  ...props
}: ProgramContextIndicatorProps) {
  const { Icon, label, legacyAccentClass } = PRESENTATION[program];
  const supplementaryDetail = normaliseProgramContextDetail(program, detail);
  return (
    <span
      {...props}
      className={cn(
        'experience-candidate-program inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-surface-base font-body font-semibold text-text-heading',
        compact ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        className,
      )}
      data-program-context={program}
    >
      <Icon
        className={cn('experience-candidate-program-icon h-4 w-4 shrink-0', legacyAccentClass)}
        aria-hidden="true"
      />
      <span className="min-w-0 break-words">{label}</span>
      {supplementaryDetail ? (
        <span className="experience-candidate-muted min-w-0 break-words font-normal text-text-muted">
          {supplementaryDetail}
        </span>
      ) : null}
      {program === 'MIXED' ? (
        <span className="inline-flex shrink-0 gap-0.5" aria-hidden="true">
          <span className="experience-candidate-program-sah h-1.5 w-1.5 rounded-full bg-statura-green-accessible" />
          <span className="experience-candidate-program-rac h-1.5 w-1.5 rounded-full bg-statura-ink" />
        </span>
      ) : null}
    </span>
  );
}
